import React, { forwardRef, useEffect, useRef, useState } from "react";
import _ from "lodash";
import { QuillOptions } from "quill";
import { App } from "antd";
import { useTranslation } from "react-i18next";

import Quill, {
  ANCHOR_CLIPBOARD_MATCHERS,
  EDITOR_TABLE_MODULE,
  replaceQuillContents,
  stripEditorOnlyTableMarkup,
} from "@/libraries/quill";
import { BaseRichTextProps, QuillRef } from "./types";
import BaseError from "../BaseError";
import {
  getMediaErrorMessage,
  IMAGE_ACCEPT,
  IMAGE_MAX_BYTES,
  IMAGE_MAX_HEIGHT,
  IMAGE_MAX_WIDTH,
  MediaKind,
  MediaUploadError,
  rewriteBlobsToMarkers,
  rewriteMarkersForDisplay,
  rewriteUrlsToMarkers,
  uploadMedia,
  useTrackMediaUpload,
} from "@/shared/media";
import { DEFAULT_RICH_TEXT_TOOLBAR, IMAGE_MAX_BYTES_BASE64 } from "./constants";

import "./styles.css";

const BaseRichText = forwardRef<HTMLDivElement, BaseRichTextProps>(
  (
    {
      value,
      onChange,
      error,
      modules = { toolbar: DEFAULT_RICH_TEXT_TOOLBAR },
      media = {},
    }: BaseRichTextProps,
    ref,
  ) => {
    const {
      useMediaUpload = false,
      maxImageSize = IMAGE_MAX_BYTES,
      maxImageWidth = IMAGE_MAX_WIDTH,
      maxImageHeight = IMAGE_MAX_HEIGHT,
    } = media;
    const quillRef = useRef<QuillRef>({
      id: _.uniqueId(),
      element: null,
    });
    const { notification } = App.useApp();
    const { t } = useTranslation();
    const [isInitial, setIsInitial] = useState(true);
    const trackUpload = useTrackMediaUpload();
    const useMediaUploadRef = useRef(useMediaUpload);
    useMediaUploadRef.current = useMediaUpload;
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const blobToIdRef = useRef<Map<string, string>>(new Map());

    const effectiveMaxImageSize = useMediaUpload
      ? maxImageSize
      : IMAGE_MAX_BYTES_BASE64;

    const validateImageDimensions = (file: File): Promise<boolean> =>
      new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          if (!e.target?.result) {
            resolve(false);
            return;
          }
          const img = new Image();
          img.onload = () => {
            if (img.width > maxImageWidth || img.height > maxImageHeight) {
              notification.error({
                message: t("form.validation.maxImageDimensionsValidation", {
                  width: maxImageWidth,
                  height: maxImageHeight,
                }),
              });
              resolve(false);
            } else {
              resolve(true);
            }
          };
          img.onerror = () => {
            notification.error({
              message: t("form.validation.invalidImageFormat"),
            });
            resolve(false);
          };
          img.src = e.target.result as string;
        };
        reader.onerror = () => {
          notification.error({
            message: t("form.validation.imageReadError"),
          });
          resolve(false);
        };
        reader.readAsDataURL(file);
      });

    const insertImageBase64 = (file: File) => {
      const quill = quillRef.current.element;
      if (!quill) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        if (!e.target?.result) return;
        const imageDataUrl = e.target.result as string;

        const img = new Image();
        img.onload = () => {
          if (img.width > maxImageWidth || img.height > maxImageHeight) {
            notification.error({
              message: t("form.validation.maxImageDimensionsValidation", {
                width: maxImageWidth,
                height: maxImageHeight,
              }),
            });
            return;
          }

          const range = quill.getSelection(true);
          quill.insertEmbed(range.index, "image", imageDataUrl);
          quill.setSelection(range.index + 1);
        };

        img.onerror = () => {
          notification.error({
            message: t("form.validation.invalidImageFormat"),
          });
        };

        img.src = e.target.result as string;
      };

      reader.onerror = () => {
        notification.error({
          message: t("form.validation.imageReadError"),
        });
      };

      reader.readAsDataURL(file);
    };

    const insertImageViaMedia = async (file: File) => {
      const quill = quillRef.current.element;
      if (!quill) return;

      const dimensionsOk = await validateImageDimensions(file);
      if (!dimensionsOk) return;

      const range = quill.getSelection(true);
      const insertionIndex = range ? range.index : quill.getLength();

      try {
        const { id } = await trackUpload(() =>
          uploadMedia(file, MediaKind.RICH_TEXT_IMAGE),
        );
        const blobUrl = URL.createObjectURL(file);
        blobToIdRef.current.set(blobUrl, id);
        quill.insertEmbed(insertionIndex, "image", blobUrl);
        quill.setSelection(insertionIndex + 1, 0);
      } catch (err) {
        const code = err instanceof MediaUploadError ? err.code : undefined;
        notification.error({ message: getMediaErrorMessage(code, t) });
      }
    };

    const handleImageFile = (file: File) => {
      if (file.size > effectiveMaxImageSize) {
        notification.error({
          message: t("form.validation.maxFileSizeValidation", {
            fileSize: effectiveMaxImageSize / 1024 / 1024,
          }),
        });
        return;
      }
      if (useMediaUploadRef.current) {
        void insertImageViaMedia(file);
      } else {
        insertImageBase64(file);
      }
    };

    const imageHandler = () => {
      const input = document.createElement("input");
      input.setAttribute("type", "file");
      input.setAttribute("accept", IMAGE_ACCEPT);
      input.click();

      input.onchange = () => {
        const file = input.files?.[0];
        if (file) {
          handleImageFile(file);
        }
      };
    };

    useEffect(() => {
      if (!quillRef.current.element) {
        const { toolbar, ...otherModules } = modules;
        const options: QuillOptions = {
          modules: {
            table: false,
            "table-better": EDITOR_TABLE_MODULE,
            clipboard: { matchers: ANCHOR_CLIPBOARD_MATCHERS },
            ...otherModules,
            toolbar: {
              container: toolbar,
              handlers: {
                image: imageHandler,
              },
            },
          },
          theme: "snow",
        };
        const quill = new Quill(`#quill-${quillRef.current.id}`, options);
        quillRef.current.element = quill;

        const editorChangeHandler = () => {
          setIsInitial(false);
          const html = stripEditorOnlyTableMarkup(quill.getSemanticHTML());
          const isEmpty = quill.getLength() === 1;
          const out = useMediaUploadRef.current
            ? rewriteUrlsToMarkers(
                rewriteBlobsToMarkers(html, blobToIdRef.current),
              )
            : html;
          onChangeRef.current(isEmpty ? "" : out);
        };

        quill.on("text-change", editorChangeHandler);

        quill.root.addEventListener("paste", (e: ClipboardEvent) => {
          const items = e.clipboardData?.items;
          if (!items) return;

          for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item && item.type.startsWith("image/")) {
              e.preventDefault();
              const file = item.getAsFile();
              if (file) {
                handleImageFile(file);
              }
            }
          }
        });
      }
    }, [maxImageSize, maxImageWidth, maxImageHeight]);

    useEffect(() => {
      if (
        quillRef.current.element &&
        isInitial &&
        value !== "" &&
        value !== null &&
        value !== undefined
      ) {
        const quill = quillRef.current.element;
        const html = useMediaUploadRef.current
          ? rewriteMarkersForDisplay(value)
          : value;
        replaceQuillContents(quill, quill.clipboard.convert({ html }));
        quill.setSelection(quill.getLength(), quill.getLength());
        setIsInitial(false);
      }
    }, [isInitial, value]);

    useEffect(() => {
      const blobs = blobToIdRef.current;
      return () => {
        blobs.forEach((_id, blobUrl) => URL.revokeObjectURL(blobUrl));
        blobs.clear();
      };
    }, []);

    return (
      <div className={`base-rich-text ${error && "base-rich-text-error"}`}>
        <div
          ref={ref}
          id={`quill-${quillRef.current.id}`}
          className="base-rich-text-quill notranslate"
          translate="no"
        ></div>
        <BaseError error={error} />
      </div>
    );
  },
);

export default BaseRichText;
